import React from 'react';
import { 
  Cpu, 
  Globe, 
  Brain, 
  Eye, 
  Code 
} from 'lucide-react';

const TechnologyFocusSection = ({ 
  formData, 
  handleInputChange, 
  errors, 
  technologyFocus,
  geographicFocus 
}) => {
  // Иконки для технологий
  const technologyIcons = {
    'machine_learning': Brain,
    'neural_networks': Cpu,
    'natural_language': Code,
    'computer_vision': Eye
  };

  // Иконки для географического фокуса
  const renderGeographyIcon = (regionId) => {
    switch (regionId) {
      case 'usa':
        return <span className="inline-block">🇺🇸</span>;
      case 'china':
        return <span className="inline-block">🇨🇳</span>;
      case 'eu':
        return <span className="inline-block">🇪🇺</span>;
      case 'global':
        return <Globe className="w-6 h-6" />;
      default:
        return <Globe className="w-6 h-6" />;
    }
  };

  return (
    <div className="mb-6 bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg border border-blue-200 dark:border-blue-800">
      <h3 className="text-base font-medium text-gray-900 dark:text-white mb-4 flex items-center">
        <Brain className="w-5 h-5 mr-2 text-indigo-500" />
        Технологический и географический фокус
      </h3>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Технологический фокус */}
        <div className="col-span-full">
          <div className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Ключевые технологии ИИ
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            {technologyFocus && technologyFocus.map((tech) => {
              const TechIcon = technologyIcons[tech.id] || Cpu;
              return (
                <label 
                  key={tech.id} 
                  className={`p-3 rounded-lg border cursor-pointer transition-colors flex flex-col items-center text-center
                    ${formData.technologyFocus.includes(tech.id)
                      ? 'bg-indigo-100 border-indigo-300 text-indigo-700 dark:bg-indigo-900/30 dark:border-indigo-700 dark:text-indigo-300'
                      : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600'
                    }`}
                >
                  <input
                    type="checkbox"
                    name="technologyFocus"
                    value={tech.id}
                    checked={formData.technologyFocus.includes(tech.id)}
                    onChange={(e) => {
                      const currentTechnologies = formData.technologyFocus;
                      const newTechnologies = e.target.checked 
                        ? [...currentTechnologies, tech.id]
                        : currentTechnologies.filter(t => t !== tech.id);
                      
                      handleInputChange({
                        target: {
                          name: 'technologyFocus',
                          value: newTechnologies
                        }
                      });
                    }}
                    className="hidden"
                  />
                  <TechIcon className={`w-6 h-6 mb-2 ${formData.technologyFocus.includes(tech.id) ? 'text-indigo-600' : 'text-gray-500'}`} />
                  <span className={`text-sm font-medium ${formData.technologyFocus.includes(tech.id) ? 'text-indigo-600' : 'text-gray-700 dark:text-gray-300'}`}>
                    {tech.name}
                  </span>
                  <span className="text-xs opacity-70 mt-1">{tech.description}</span>
                </label>
              );
            })}
          </div>
          {errors.technologyFocus && (
            <p className="mt-1 text-xs text-red-500 dark:text-red-400">
              {errors.technologyFocus}
            </p>
          )}
        </div>

        {/* Географический фокус */}
        <div className="col-span-full">
          <div className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Географический охват инвестиций
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            {geographicFocus && geographicFocus.map((region) => (
              <label 
                key={region.id} 
                className={`p-3 rounded-lg border cursor-pointer transition-colors flex flex-col items-center text-center
                  ${formData.geographicFocus.includes(region.id)
                    ? 'bg-indigo-100 border-indigo-300 text-indigo-700 dark:bg-indigo-900/30 dark:border-indigo-700 dark:text-indigo-300'
                    : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600'
                  }`}
              >
                <input
                  type="checkbox"
                  name="geographicFocus"
                  value={region.id}
                  checked={formData.geographicFocus.includes(region.id)}
                  onChange={(e) => {
                    const currentRegions = formData.geographicFocus;
                    const newRegions = e.target.checked 
                      ? [...currentRegions, region.id]
                      : currentRegions.filter(r => r !== region.id);
                    
                    handleInputChange({
                      target: {
                        name: 'geographicFocus',
                        value: newRegions
                      }
                    });
                  }}
                  className="hidden"
                />
                <div className="mb-2">
                  {renderGeographyIcon(region.id)}
                </div>
                <span className={`text-sm font-medium ${formData.geographicFocus.includes(region.id) ? 'text-indigo-600' : 'text-gray-700 dark:text-gray-300'}`}>
                  {region.name}
                </span>
                <span className="text-xs opacity-70 mt-1">{region.description}</span>
              </label>
            ))}
          </div>
          {errors.geographicFocus && (
            <p className="mt-1 text-xs text-red-500 dark:text-red-400">
              {errors.geographicFocus}
            </p>
          )}
        </div>
      </div>

      {/* Информационный блок о технологиях */}
      <div className="mt-4 bg-green-50 dark:bg-green-900/20 p-4 rounded-lg border border-green-100 dark:border-green-800">
        <div className="flex items-start">
          <Cpu className="w-5 h-5 text-green-600 dark:text-green-400 mr-3 mt-1" />
          <div>
            <h4 className="text-sm font-medium text-green-700 dark:text-green-300 mb-1">
              Стратегический выбор технологий
            </h4>
            <p className="text-xs text-green-600 dark:text-green-400">
              Выбирая технологический и географический фокус, вы создаете 
              целевую инвестиционную стратегию. Диверсификация по технологиям 
              и регионам помогает снизить риски и использовать глобальный 
              потенциал развития искусственного интеллекта.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TechnologyFocusSection;